/* eslint-disable */
// @ts-nocheck

import { isProxy, toRaw } from "vue";
import { getQuoteLineUnitPrice, isQuoteAdjustmentLineItem } from "./quotePricing";
import { LargeLengthUnits } from "@/libs/units/length";
import { convert, ToUnit } from "@/libs/units";
import {
  SkBillable,
  SkMaterial,
  AllQuoteFieldsFragment as QuoteType,
  QuoteLineItemFieldsFragment as QuoteLineItemType,
  Quotes,
  SkEquipmentTypeConversion,
  Quote_Line_Item_Category_Dataset_Enum as Category,
  Quote_Line_Item_Subcategory_Dataset_Enum as SubCategory,
  Quote_Sk_Job_Type_Dataset_Enum as SKJobType
} from "gql/api";

// Add interface for quote rows with children (for grouped mode)
export interface QuoteWithChildren extends Quotes {
  children?: QuoteWithChildren[];
}

export enum SgqJobTypes {
  SALE = "sale",
  RENTAL = "rental"
}

export enum SgqQuoteTypes {
  INSTALLATION = "installation",
  MONITORING = "monitoring"
}

export enum QuoteTermsTemplate {
  HGC_SALE_TRADE = "hgc_sale_trade",
  HGC_WELL_TEST = "hgc_well_test",
  HGC_MONITORING = "hgc_monitoring",
  HESI_SALE_TRADE = "hesi_sale_trade",
  HESI_WELL_TEST = "hesi_well_test",
  HESI_MONITORING = "hesi_monitoring"
}

type QuoteCountryCode = "CA" | "US";

const quoteTermsTemplatesByJobType: Record<
  string,
  Record<QuoteCountryCode, QuoteTermsTemplate>
> = {
  [SKJobType.Spyglass_1126619]: {
    CA: QuoteTermsTemplate.HGC_SALE_TRADE,
    US: QuoteTermsTemplate.HESI_SALE_TRADE
  },
  [SKJobType.Spyglass_1092000]: {
    CA: QuoteTermsTemplate.HGC_WELL_TEST,
    US: QuoteTermsTemplate.HESI_WELL_TEST
  },
  [SKJobType.Spyglass_1092014]: {
    CA: QuoteTermsTemplate.HGC_MONITORING,
    US: QuoteTermsTemplate.HESI_MONITORING
  },
  [SKJobType.Spyglass_1092002]: {
    CA: QuoteTermsTemplate.HGC_MONITORING,
    US: QuoteTermsTemplate.HESI_MONITORING
  }
};

export function getQuoteTermsTemplate(
  jobType: SKJobType | string | null | undefined,
  countryCode: string | null | undefined
): QuoteTermsTemplate | null {
  if (
    !jobType ||
    (countryCode !== "CA" && countryCode !== "US")
  ) {
    return null;
  }

  return quoteTermsTemplatesByJobType[jobType]?.[countryCode] ?? null;
}

export enum JobTypeServiceMaterialNumbers {
  DAILY = 1095538,
  MONTHLY = 1095537,
  DIRECT_SALE_MONITORING_FEE = 1092014,
  RENTAL_MONITORING_FEE = 1092002
}

const dailyLongDescFallback = "Daily Rental Fee";
const monthlyLongDescFallback = "Monthly Rental Fee";
const directSaleMonitoringFeeLongDescFallback = "SESP DIRECT SALE BOM - MONITORING FEE";
const rentalMonitoringFeeLongDescFallback = "SESP RENTAL BOM - MONITORING FEE";
export enum JobTypeServiceMaterialLongDesc {
  DAILY = dailyLongDescFallback,
  MONTHLY = monthlyLongDescFallback,
  DIRECT_SALE_MONITORING_FEE = directSaleMonitoringFeeLongDescFallback,
  RENTAL_MONITORING_FEE = rentalMonitoringFeeLongDescFallback
}

export enum JobTypeServiceMaterialShortDesc {
  DAILY = "AL ESP WELL TEST RENTAL RATE, MONTHLY",
  MONTHLY = "AL ESP WELL TEST RENTAL RATE, DAILY",
  DIRECT_SALE_MONITORING_FEE = "SESP DIRECT SALE BOM - MONITORING FEE",
  RENTAL_MONITORING_FEE = "SESP RENTAL BOM - MONITORING FEE"
}

export function createQueryString<CustomQueryParams extends Record<string, any>>(
  params:
    | string
    | string[][]
    | Record<string, string>
    | URLSearchParams
    | CustomQueryParams
    | undefined
): string {
  const SearchParams = new URLSearchParams(params);
  return SearchParams.toString();
}

export function createObjectFromQueryString(str): any {
  // Ex1.
  //
  // let params = new URLSearchParams('abc=foo&def=%5Basf%5D&xyz=5');
  // params.get("abc"); // "foo"

  // Ex2.
  //
  // const urlParams = new URLSearchParams('abc=foo&def=%5Basf%5D&xyz=5');
  // const params = Object.fromEntries(urlParams);

  const SearchParams = new URLSearchParams(str);

  const params = Object.fromEntries(SearchParams);

  return params;
}

/** ERP pricing condition type for contract prices (ZPCP). */
export const CONTRACT_PRICING_TYPE = 2;

/**
 * Numeric ERP pricing condition type. ECC returned a number; the canonical S4
 * read path returns an enum object such as
 * `{ value: 2, label: "Contract", sap_value: "ZPCP", name: "contract" }`.
 */
export function getPricingTypeValue(type: unknown): number | null {
  const raw = type !== null && typeof type === "object"
    ? (type as { value?: unknown }).value
    : type;
  if (raw === null || raw === undefined || raw === "") {
    return null;
  }
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

/** True when a material's expanded pricing is a contract price. */
export function isContractPricing(pricing: { type?: unknown } | null | undefined): boolean {
  if (!pricing) {
    return false;
  }
  const type = pricing.type as { sap_value?: unknown; name?: unknown } | unknown;
  if (type !== null && typeof type === "object") {
    const { sap_value: sapValue, name } = type as { sap_value?: unknown; name?: unknown };
    if (sapValue === "ZPCP" || name === "contract") {
      return true;
    }
  }
  return getPricingTypeValue(pricing.type) === CONTRACT_PRICING_TYPE;
}

const isUsablePriceListNumber = (value: unknown) => {
  const text = value === null || value === undefined ? "" : String(value).trim();
  return !!text && text !== "undefined" && text !== "null";
};

export type QuotePricingContext = {
  country: string | null;
  missing: string[];
  priceListNumber: string | null;
  pricingDate: string | null;
};

/**
 * Pricing inputs required before Job Type / Quote Type can add service
 * charges. Prefer the live Sales Info form so selecting a ship-to or price list
 * unlocks the dropdowns immediately; fall back to the saved quote. This used to
 * read only the saved quote, so users had to save (or re-save the ship-to)
 * before they could pick a Job Type.
 */
export function getQuotePricingContext(advancedSearch: any, quote: any): QuotePricingContext {
  const country = advancedSearch?.wellAddressSearch?.address?.region?.country?.code
    || quote?.erp_well_country
    || null;
  const formPriceList = advancedSearch?.priceListSearch?.number;
  const priceListNumber = isUsablePriceListNumber(formPriceList)
    ? String(formPriceList)
    : isUsablePriceListNumber(quote?.erp_price_list_number)
      ? String(quote.erp_price_list_number)
      : null;
  const pricingDate = fixDate(advancedSearch?.pricingDate || quote?.pricing_date) || null;

  const missing: string[] = [];
  if (!country) {
    missing.push("Ship To / Well (country)");
  }
  if (!priceListNumber) {
    missing.push("Price List");
  }
  if (!pricingDate) {
    missing.push("Pricing Date");
  }

  return { country, missing, priceListNumber, pricingDate };
}

/** Margin applied to new equipment lines without a contract price. */
export const DEFAULT_EQUIPMENT_MARGIN = 60;

const roundCurrency = (value: number) => Math.round(value * 100) / 100;

/** Selling price for a base price and margin %, matching the quote table. */
export function getSellingPriceFromMargin(erpPrice: number, margin: number): number {
  if (!erpPrice || !margin) {
    return roundCurrency(erpPrice || 0);
  }
  const price = erpPrice / (1 - margin / 100);
  return Number.isFinite(price) ? roundCurrency(price) : 0;
}

type RefreshableLineItem = {
  erp_material_number?: string | null;
  erp_price?: number | string | null;
  margin?: number | string | null;
  price_override?: number | string | null;
};

type RefreshedMaterial = {
  number?: string | null;
  pricing?: { price?: string | number | null; type?: unknown } | null;
};

/**
 * Apply refreshed ERP pricing to quote lines in place and describe the changes.
 *
 * Matches the per-line pricing-date refresh: lines with a margin keep it and
 * get a new selling price; lines without one sell at the new base price.
 * With a contract on the quote, a contract-priced line still carrying the
 * default margin is reset to 0%. SK-5234 applied that default to contract
 * prices because the canonical S4 pricing type was not recognised.
 */
export function applyRefreshedLineItemPricing<T extends RefreshableLineItem>(
  lineItems: T[],
  materials: RefreshedMaterial[],
  { contractActive }: { contractActive: boolean }
): { changedItems: T[]; messages: string[] } {
  const materialsByNumber = new Map(
    (materials || []).filter((m) => m?.number).map((m) => [m.number as string, m])
  );
  const changedItems: T[] = [];
  const messages: string[] = [];

  for (const item of lineItems || []) {
    const material = item?.erp_material_number
      ? materialsByNumber.get(item.erp_material_number)
      : undefined;
    const rawPrice = material?.pricing?.price;
    if (rawPrice === null || rawPrice === undefined || rawPrice === "") {
      continue;
    }
    const newPrice = roundCurrency(Number(rawPrice));
    if (!Number.isFinite(newPrice)) {
      continue;
    }

    const oldPrice = roundCurrency(Number(item.erp_price) || 0);
    const oldMargin = Number(item.margin) || 0;
    const resetContractMargin = contractActive
      && isContractPricing(material?.pricing)
      && oldMargin === DEFAULT_EQUIPMENT_MARGIN;
    const newMargin = resetContractMargin ? 0 : oldMargin;

    if (newPrice === oldPrice && !resetContractMargin) {
      continue;
    }

    item.erp_price = newPrice;
    item.margin = newMargin;
    item.price_override = getSellingPriceFromMargin(newPrice, newMargin);
    changedItems.push(item);

    const details: string[] = [];
    if (newPrice !== oldPrice) {
      details.push(`price ${oldPrice.toFixed(2)} to ${newPrice.toFixed(2)}`);
    }
    if (resetContractMargin) {
      details.push(`contract price margin ${oldMargin}% to 0%`);
    }
    messages.push(`${item.erp_material_number}: ${details.join(", ")}`);
  }

  return { changedItems, messages };
}

type QuoteRouteLike = {
  matched?: ReadonlyArray<{ name?: string | symbol | null }>;
};

/** Parent route records that render the quote editor (see router/quotes.ts). */
const QUOTE_EDITOR_ROUTE_NAMES = new Set(["EditQuote", "QuoteNew"]);

/** True while the quote editor (existing or new quote) is the active route. */
export function isQuoteEditorRoute(route: QuoteRouteLike | null | undefined): boolean {
  return (route?.matched || []).some(
    (record) => typeof record?.name === "string" && QUOTE_EDITOR_ROUTE_NAMES.has(record.name)
  );
}

/**
 * Quote ID to navigate to after the version dropdown changes, or null.
 * Never navigates away from a leaving editor or to the "new" placeholder.
 */
export function getQuoteVersionNavigationTarget(
  version: string | null | undefined,
  routeQuoteId: string | string[] | null | undefined,
  onQuoteEditorRoute: boolean
): string | null {
  const currentQuoteId = Array.isArray(routeQuoteId) ? routeQuoteId[0] : routeQuoteId;
  if (!onQuoteEditorRoute || !version || version === "new" || version === currentQuoteId) {
    return null;
  }
  return version;
}

export type QuoteWellAddressQueryInput = {
  addressId?: string | number | null;
  customerId?: string | number | null;
  wellId?: string | number | null;
  wellUwi?: string | null;
};

/**
 * Query string for the ERP well-address lookup used when generating a quote
 * from a sizing scenario, and when restoring a quote's missing address.
 *
 * Do not send `include_null_addresses`. At the ERP it is an
 * `address__name__isnull` filter, so `True` returns only rows without an
 * address. That dropped the ship-to address, region, and country for
 * well-based quotes and disabled Job Type and Quote Type. Omitting the filter
 * returns rows with and without an address.
 */
export function buildQuoteWellAddressQueryString({
  addressId,
  customerId,
  wellId,
  wellUwi,
}: QuoteWellAddressQueryInput): string {
  const params: Record<string, string> = {};
  if (customerId) {
    params.customer_name = String(customerId);
  }
  if (addressId) {
    params.address_id = String(addressId);
  }
  if (wellId) {
    params.well_id = String(wellId);
  }
  if (wellUwi) {
    params.uwi = String(wellUwi);
  }
  params.expand = "well,address.region.country,customer";
  params.deleted = "False";
  params.ordering = "-created";
  return createQueryString(params);
}

/** Normalize an ERP id from sizing data; "[TBD]" wells use "0". */
export function normalizeErpId(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const id = String(value).trim();
  return id && id !== "0" && id !== "null" && id !== "undefined" ? id : null;
}

export type QuoteNwa = {
  al_name?: string | null;
  display_name?: string | null;
  id?: string | number | null;
  name?: string | null;
};

/**
 * NWA label for users. ERP's `display_name` is the AL Name (original ECC
 * name) when set, otherwise the S4 name. Store and send the NWA id; names are
 * for display only.
 */
export function getNwaDisplayName(nwa: QuoteNwa | null | undefined): string {
  return String(nwa?.display_name || nwa?.al_name || nwa?.name || "").trim();
}

/** Dropdown label: AL Name, the S4 name when different, and the NWA id. */
export function getNwaOptionLabel(nwa: QuoteNwa | null | undefined): string {
  const display = getNwaDisplayName(nwa);
  const s4Name = String(nwa?.name || "").trim();
  const withS4Name = s4Name && s4Name !== display ? `${display} (${s4Name})` : display;
  return nwa?.id ? `${withS4Name} · ${nwa.id}` : withS4Name;
}

/** SK/ERP NWA lookup by id (canonical S4 data under ERP routing). */
export function buildNwaByIdQueryString(nwaId: string | number | null | undefined): string | null {
  const id = normalizeErpId(nwaId);
  return id ? createQueryString({ id, deleted: "False", expand: "country" }) : null;
}

export type QuoteNwaResolution = {
  /** True when the canonical lookup ran and answered (found or not). */
  looked_up: boolean;
  nwa: QuoteNwa | null;
};

/**
 * Quote NWA fields from a planning order and its canonical NWA.
 *
 * Planning orders come from historical SK jobs, whose `natural_work_area` is
 * the name stored at job time (often a retired ECC name). The id is what ERP
 * shares between ECC and S4, so resolve it against the canonical NWA. When
 * the lookup answers without a match the NWA is no longer valid and is left
 * empty; when the lookup could not run, keep the planning order's name.
 */
export function getQuoteNwaFields(
  planningOrder: QuotePlanningOrder | null | undefined,
  resolution: QuoteNwaResolution | null | undefined
) {
  const nwaId = normalizeErpId(planningOrder?.nwa_id);
  if (!nwaId) {
    return { erp_nwa_id: null, erp_nwa_name: null };
  }
  if (resolution?.looked_up) {
    const name = getNwaDisplayName(resolution.nwa);
    return name && resolution.nwa
      ? { erp_nwa_id: String(resolution.nwa.id ?? nwaId), erp_nwa_name: name }
      : { erp_nwa_id: null, erp_nwa_name: null };
  }
  return { erp_nwa_id: nwaId, erp_nwa_name: planningOrder?.natural_work_area || null };
}

export type QuotePlanningOrderQueryInput = {
  addressId?: string | number | null;
  customerId?: string | number | null;
  wellId?: string | number | null;
};

/**
 * Query string for SK `/api/v2/plo`, or null when SK would reject it.
 *
 * SK requires `customer_id` plus `well_id` or `address_id`, and treats any
 * *present* key as a filter. Sending `well_id=` or `address_id=` empty matches
 * only jobs with a blank value, which returned no planning order for many
 * well-only and ship-to-only quotes. Empty values are therefore omitted.
 */
export function buildQuotePlanningOrderQueryString({
  addressId,
  customerId,
  wellId,
}: QuotePlanningOrderQueryInput): string | null {
  const customer = normalizeErpId(customerId);
  const well = normalizeErpId(wellId);
  const address = normalizeErpId(addressId);
  if (!customer || (!well && !address)) {
    return null;
  }
  const params: Record<string, string> = { customer_id: customer };
  if (well) {
    params.well_id = well;
  }
  if (address) {
    params.address_id = address;
  }
  return createQueryString(params);
}

export type QuotePlanningOrder = {
  contract?: string | null;
  currency?: string | null;
  natural_work_area?: string | null;
  nwa_id?: string | null;
  planning_order?: string | null;
  plant_erp_id?: string | null;
  plant_name?: string | null;
  price_list_name?: string | null;
  price_list_number?: string | null;
};

/**
 * Most useful planning-order row. SK sorts newest first, but some rows have no
 * planning order number or price list; prefer the first complete one.
 */
export function pickQuotePlanningOrder<T extends QuotePlanningOrder>(
  rows: Array<T | null | undefined> | null | undefined
): T | null {
  const candidates = (rows || []).filter((row): row is T => !!row);
  return candidates.find((row) => row.planning_order && row.price_list_number)
    || candidates.find((row) => row.price_list_number)
    || candidates[0]
    || null;
}

export type SizingQuoteErpHeaderInput = {
  addressId?: string | number | null;
  customerId?: string | number | null;
  /** Canonical NWA for the planning order's `nwa_id`; see resolveQuoteNwa. */
  nwa?: QuoteNwaResolution | null;
  planningOrder?: QuotePlanningOrder | null;
  sizingCustomerName?: string | null;
  sizingWellName?: string | null;
  sizingWellUwi?: string | null;
  today: string;
  wellAddress?: any;
  wellId?: string | number | null;
};

/**
 * ERP header fields for a quote generated from a sizing scenario (SK-5231).
 *
 * The header comes from the customer's planning order for the well/ship-to,
 * the same source the Sales Info form uses when a ship-to is selected. It
 * used to come from the first SK job returned by a filter SK ignored, so
 * quotes got another customer's NWA, plant, price list, and contract.
 */
export function buildSizingQuoteErpHeader({
  addressId,
  customerId,
  nwa,
  planningOrder,
  sizingCustomerName,
  sizingWellName,
  sizingWellUwi,
  today,
  wellAddress,
  wellId,
}: SizingQuoteErpHeaderInput) {
  const customer = normalizeErpId(customerId);
  const well = normalizeErpId(wellId);
  const sizingAddress = normalizeErpId(addressId);
  // Only a sizing well or ship-to gives ERP context; the well-address lookup
  // can then fill in the ship-to address for a well.
  const hasErpContext = Boolean(customer && (well || sizingAddress));
  const address = sizingAddress || normalizeErpId(wellAddress?.address?.id);
  const plo = hasErpContext ? planningOrder : null;
  const erpAddress = hasErpContext ? wellAddress?.address : null;

  return {
    currency: plo?.currency || null,
    erp_contract_number: plo?.contract || null,
    erp_customer_number: customer,
    // Prefer the canonical S4 sold-to and well data from the well-address
    // lookup; fall back to what the sizing stored.
    erp_customer_name: (hasErpContext && String(wellAddress?.customer?.name || "").trim())
      || sizingCustomerName
      || null,
    erp_well_name: (hasErpContext && wellAddress?.well?.name) || sizingWellName || null,
    erp_well_uwi: sizingWellUwi || (hasErpContext && wellAddress?.well?.uwi) || null,
    ...getQuoteNwaFields(plo, nwa),
    erp_plant_id: plo?.plant_erp_id || null,
    erp_plant_name: plo?.plant_name || null,
    erp_price_list_name: plo?.price_list_name || null,
    erp_price_list_number: plo?.price_list_number || null,
    erp_well_country: erpAddress?.region?.country?.code || null,
    erp_well_region: erpAddress?.region?.code || null,
    erp_well_id: well,
    erp_address_id: hasErpContext ? address : null,
    erp_address_name: erpAddress?.name || null,
    erp_full_address: erpAddress?.full_address || null,
    pricing_date: today,
    proposed_start_date: today,
    sk_planning_order: plo?.planning_order || null,
  };
}

type QuoteWellAddressFields = {
  erp_address_id?: string | null;
  erp_address_name?: string | null;
  erp_full_address?: string | null;
  erp_well_country?: string | null;
  erp_well_id?: string | null;
  erp_well_region?: string | null;
};

/**
 * Missing quote address fields that can be restored from an ERP well-address
 * row. Returns null when the quote already has a country, the row has no
 * country, or the row belongs to a different address or well. Existing values
 * are never overwritten.
 */
export function getMissingQuoteWellAddressFields(
  quote: QuoteWellAddressFields | null | undefined,
  wellAddress: any
): QuoteWellAddressFields | null {
  const address = wellAddress?.address;
  const countryCode = address?.region?.country?.code;
  if (!quote || quote.erp_well_country || !countryCode) {
    return null;
  }

  const addressMismatch = quote.erp_address_id && address?.id
    && String(quote.erp_address_id) !== String(address.id);
  const wellMismatch = quote.erp_well_id && wellAddress?.well?.id
    && String(quote.erp_well_id) !== String(wellAddress.well.id);
  if (addressMismatch || wellMismatch) {
    return null;
  }

  const patch: QuoteWellAddressFields = { erp_well_country: countryCode };
  if (!quote.erp_well_region && address?.region?.code) {
    patch.erp_well_region = address.region.code;
  }
  if (!quote.erp_address_id && address?.id) {
    patch.erp_address_id = String(address.id);
  }
  if (!quote.erp_address_name && address?.name) {
    patch.erp_address_name = address.name;
  }
  if (!quote.erp_full_address && address?.full_address) {
    patch.erp_full_address = address.full_address;
  }
  return patch;
}

export type BuildMaterialSearchQueryParamsInput = {
  advancedSearch?: any;
  filters: any;
  maxItems: number;
  quote?: any;
  quoteLineItemsWithEquipment?: Array<{ erp_material_number?: string | null }>;
  sortField: string;
  sortOrder: number;
};

export function buildMaterialSearchQueryParams({
  advancedSearch,
  filters,
  maxItems,
  quote,
  quoteLineItemsWithEquipment = [],
  sortField,
  sortOrder,
}: BuildMaterialSearchQueryParamsInput) {
  const materialSearch = filters.materialSearchInput ?? "";
  const hierarchyInput = filters.productHierarchyInput ?? "";
  const orderingFields = sortField === "description"
    ? ["description", "number"]
    : [sortField, "description", "number"].filter((field, index, fields) => {
      return field && fields.indexOf(field) === index;
    });
  const ordering = orderingFields
    .map((field) => sortOrder === 1 ? field : `-${field}`)
    .join(",");

  const params: Record<string, any> = {
    deleted: "False",
    exclude_trade_in: ["all", "trade_in"].includes(filters.materialTypeSelection) ? "False" : "True",
    expand: "pricing,inventory",
    // Opt in to quantity-based material stock for both filtering and display.
    // ERP selects ECC or canonical/S4 inventory and includes pending async
    // quantities when its overlay is active. No separate async request or
    // response shape is needed here.
    // Requires ERP #1666 and combined SK #6577 (which supersedes SK #6583).
    // Keep the displayed-availability safeguard for staggered deployments
    // where SK still strips this parameter or ERP does not yet implement it.
    inventory_source: "material",
    page: filters.page,
    page_size: maxItems,
    plant: advancedSearch?.salesDistrictSearch?.id,
    ordering,
  };

  // Only constrain by stock / pricing when the user opts in; omit the param
  // entirely when off so the ERP filter behavior stays predictable.
  //
  // Use `in_stock` (not `has_stock`) to match how SK searches stock today.
  // With `inventory_source=material`, ERP #1666 uses positive net available
  // stock, excluding "1005", field-truck ("7*"), and intercom-transit locations,
  // before pagination. Older ERP equipment checks can disagree with expanded
  // `inventory.available`, so quote results also check displayed availability.
  // `has_stock` is an S4-only filter with none of those
  // exclusions and is silently ignored on ECC, so it showed 0-stock rows and
  // emptied broad searches.
  if (filters.inStockFilter) {
    params.in_stock = "True";
  }
  if (filters.pricedFilter) {
    params.has_pricing = true;
  }

  // Material type is a single, mutually-exclusive selection. Each category maps
  // to an exact set of ERP material_type codes; "All" omits the type constraint.
  const matCodes = {
    defaults: ["ZFSP", "ZFST", "ZSER", "ZFRP", "ZEQP"],
    finishedGoods: ["ZFCH"],
    smallParts: ["ZSCP"],
    rental: ["ZRNV"],
    customerOwned: ["ZCUS"],
    tradeIn: ["ZTRD"],
  };

  const materialTypesBySelection: Record<string, string[]> = {
    sale_finished: [...matCodes.defaults, ...matCodes.finishedGoods],
    small_parts: [...matCodes.defaults, ...matCodes.smallParts],
    customer_owned: [...matCodes.customerOwned],
    rental: [...matCodes.rental],
    trade_in: [...matCodes.tradeIn],
  };

  const materialTypes = filters.materialTypeSelection === "all"
    ? undefined
    : materialTypesBySelection[filters.materialTypeSelection]
      || materialTypesBySelection.sale_finished;

  // NOTE: do not scope "Customer Owned" with `customer_related`. That param
  // joins through cable serial inventory (ECC) / cable inventory (S4), which is
  // only appropriate when specifically searching for cable, and it narrows to
  // specific owned serial numbers. Quote material search is for materials only
  // (not serialized equipment / cable serials), so Customer Owned is simply the
  // ZCUS material type searched against standard material inventory.

  if (materialTypes) {
    params.material_type__in = materialTypes.join(",");
  }

  if (filters.standardOfferingFilter) {
    params.number__in = cleanNull(quoteLineItemsWithEquipment)
      .map((qli) => qli.erp_material_number)
      .filter((matNumber): matNumber is string => !!matNumber && matNumber !== "custom");
  }

  // Do not add a material-number prefix constraint for numeric input. ERP
  // `search` already matches partial numbers anywhere in the material number;
  // requiring `number__istartswith` hid mid-number matches (545003 ->
  // 900545003) and prefixed variants such as ZCUS900545003 / ZRNV900545003.
  if (filters.basicSearch) {
    params.advanced_search = materialSearch;
    params.excludes = filters.excludeInput;
  } else {
    params.search = materialSearch;
  }

  if (hierarchyInput.trim()) {
    const hierarchySearch = hierarchyInput
      .split(",")
      .map((hierarchy) => hierarchy.trim())
      .filter(Boolean)
      .join(",");

    if (hierarchySearch.includes(",")) {
      params.prod_hierarchies = hierarchySearch;
    } else {
      params.prod_hierarchy = hierarchySearch;
    }
  }

  if (advancedSearch?.wellAddressSearch?.address?.region?.country?.code) {
    params.country = advancedSearch.wellAddressSearch.address.region.country.code;
  }

  if (advancedSearch?.priceListSearch?.number && advancedSearch.priceListSearch.number !== "undefined") {
    params.price_list = advancedSearch.priceListSearch.number;
  }

  if (advancedSearch?.pricingDate) {
    params.pricing_date = fixDate(advancedSearch.pricingDate);
  }

  if (advancedSearch?.contractSearch?.number) {
    params.contract = advancedSearch.contractSearch.number;
  }

  if (filters.cableFilter) {
    params.cable_in_stock = "True";
    params.expand = `${params.expand},cable_inventory`;
  }

  // By default, limit results to materials extended to the quote's plant /
  // sales district. The "Search All Plants" toggle (allPlantsFilter) lifts this
  // restriction. `extended_to` takes plant IDs (lookup "in") at the ERP.
  if (!filters.allPlantsFilter) {
    const extendedToPlant = advancedSearch?.salesDistrictSearch?.id || quote?.erp_plant_id;
    if (extendedToPlant) {
      params.extended_to = extendedToPlant;
    }
  }

  return params;
}

/**
 * Plant IDs the ERP uses to park materials that must not be sold/quoted
 * (e.g. the "DO NOT USE" plant). `extended_to_plants` from the materials API is
 * an array of plant *IDs* (slug_field="id"), not names, so exclusion must be by
 * ID. These are runtime/ERP data, so the list is injected by the caller.
 */
export const DEFAULT_EXCLUDED_PLANT_IDS: string[] = [];

/**
 * Description tokens that flag a material as obsolete / discontinued.
 * The ERP has no obsolete boolean (these materials stay active=true), so the
 * only quote-side signal is the description text, e.g.:
 *   "OBSOLETE WITHOUT REPLACEMENT - PUMP, ..."
 *   "OBS, W/O REPLACEMENT - PMP,400,SF1750,74"
 * Matched as whole words to limit false positives.
 * OTD alone is not an exclusion: ERP search can return those materials for
 * quoting, including 102946127. Keep any replacement information visible in
 * the description rather than silently removing the result.
 */
const OBSOLETE_DESCRIPTION_PATTERN = /\b(obsolete|obs)\b/i;

/**
 * True when a material is extended to one of the excluded ("do not use") plant
 * IDs. Such materials should never be quotable, even if also extended to valid
 * plants. `extended_to_plants` contains plant IDs (not names).
 */
export function isDoNotUsePlantMaterial(
  material: SkMaterial,
  excludedPlantIds: string[] = DEFAULT_EXCLUDED_PLANT_IDS
): boolean {
  if (!excludedPlantIds || excludedPlantIds.length === 0) {
    return false;
  }
  const excluded = new Set(excludedPlantIds.map((id) => String(id)));
  return (material?.extended_to_plants || []).some(
    (plant) => !!plant && excluded.has(String(plant))
  );
}

/**
 * True when a material's description marks it as obsolete / discontinued.
 */
export function isObsoleteMaterial(material: SkMaterial): boolean {
  const haystack = `${material?.description || ""} ${material?.long_description || ""}`;
  return OBSOLETE_DESCRIPTION_PATTERN.test(haystack);
}

/**
 * True when a material must be hidden from quote search results: either it is
 * obsolete or it is extended to an excluded ("do not use") plant.
 */
export function isUnsellableMaterial(
  material: SkMaterial,
  excludedPlantIds: string[] = DEFAULT_EXCLUDED_PLANT_IDS
): boolean {
  return isObsoleteMaterial(material) || isDoNotUsePlantMaterial(material, excludedPlantIds);
}

/**
 * Quote-side stopgap filter. Removes obsolete materials and materials extended
 * to an excluded ("do not use") plant from a page of search results.
 *
 * NOTE: this operates on the current (already paginated) page of results, so
 * server-reported counts become approximate and a page may shrink. The correct
 * long-term fix is an ERP/SK change (real `extended_to` exclusion + an obsolete
 * flag + stock-aware ordering). See ~/summit/erp-service-integration.
 */
export function filterUnsellableMaterials<T extends SkMaterial>(
  materials: T[],
  excludedPlantIds: string[] = DEFAULT_EXCLUDED_PLANT_IDS
): T[] {
  return (materials || []).filter((material) => !isUnsellableMaterial(material, excludedPlantIds));
}

/**
 * Available stock quantity for a material at the searched plant, as a number.
 * Returns 0 when inventory is missing or unparseable.
 */
export function getMaterialStockAvailable(material: SkMaterial): number {
  const available = parseFloat(material?.inventory?.available ?? "");
  return Number.isFinite(available) ? available : 0;
}

/**
 * True when the material has available stock at the searched plant.
 */
export function isMaterialInStock(material: SkMaterial): boolean {
  return getMaterialStockAvailable(material) > 0;
}

/** Enforce stock filters against the same expanded quantities the quote displays. */
export function matchesMaterialStockFilters(
  material: SkMaterial,
  filters: { inStockFilter: boolean; cableFilter: boolean }
): boolean {
  const cableAvailable = Number(material?.cable_inventory?.available);
  return (!filters.inStockFilter || isMaterialInStock(material))
    && (!filters.cableFilter || (Number.isFinite(cableAvailable) && cableAvailable > 0));
}

/**
 * Stable sort that floats in-stock materials to the top while preserving the
 * incoming order within the in-stock and out-of-stock groups.
 *
 * NOTE: quote-side stopgap — this only reorders the current page. True global
 * "in-stock first" ordering needs an ERP/SK ordering change.
 */
export function sortInStockFirst<T extends SkMaterial>(materials: T[]): T[] {
  return (materials || [])
    .map((material, index) => ({ material, index }))
    .sort((a, b) => {
      const stockDelta = Number(isMaterialInStock(b.material)) - Number(isMaterialInStock(a.material));
      return stockDelta !== 0 ? stockDelta : a.index - b.index;
    })
    .map((entry) => entry.material);
}

export const isEmpty = (val) => {
  return (
    !val ||
    val === undefined ||
    val === null ||
    JSON.stringify(val) === "{}"
  );
};

/**
* Type guard strategy, return a non nullable value.
*
* :param val: any type T  (inferred unless called as in example)
* :return type: val is NonNullable<T>
*
* Example calling with type definition:
*   `notNull<number | null | undefined>(qli.quantity)`
*/
export function notNull<T>(val: T): val is NonNullable<T> {
  return (
    val !== null &&
    val !== undefined
  )
};

export function returnNoNull<T>(val: T | null | undefined):T {
  if (val === null || val === undefined) {
    if (typeof val === "number") {
      return 0;
    } else if (typeof val === "string") {
      return "";
    }
  }
  return val;
};


export function cleanNull<T>(arr: (T | null | undefined)[]): T[] {
  return arr.filter((m): m is T => m !== null)
};

export function stringIncludes(str1: string, str2: string): boolean {
  if (str1 === null || str2 === null || str1 === "" || str2 === "") {
    return false;
  }
  return str1.toLowerCase().includes(str2.trim().toLowerCase());
}

export const autoVal = (
  value,
  accessor,
  empty = false,
  doubleAccessor = null
) => {
  if (typeof value !== "object") {
    // not selected (search value)

    if (empty) {
      return "";
    } else {
      return value;
    }
  } else {
    // selected (selected obj value)
    if (doubleAccessor === null) {
      return value ? value[accessor] : "";
    } else {
      return value ? value[accessor][doubleAccessor] : "";
    }
  }
};

export const createFilteredListGeneric = (
  dataArray,
  accessor,
  text,
  doubleAccessor = ""
) => {
  /*
    dataArray:  Array to search and select
    accessor:   Property on array item to compare
    text:       Search text to compare

    Explain: Yes, technically filtered list does not need to even do this
             filter as it's taken care of by sk in the api using query params
             however the list needs to be recreated for the auto completes to
             register and somehow they work much better and faster with this code.
  */

  let filteredList;
  if (text === null || text === undefined || text.trim().length === 0) {
    // Empty search means dropdown click.
    filteredList = dataArray.filter(item => {
      // XXX Don't filter out empty items for now.
      // if (item === null || item.id === null) {
      //  // Filter out empty items
      //  return false;
      // }
      return true;
    });
  } else {
    filteredList = dataArray;
    // TODO: instead of remove items, move them to the top of the list
    //   .filter((item) => {
    //   if (doubleAccessor.length === 0) {
    //     return stringIncludes(item[accessor], text);
    //   } else if (doubleAccessor.length > 0) {
    //     return stringIncludes(item[accessor][doubleAccessor], text);
    //   }
    // });
  }

  return filteredList;
};

export const getSpyGlassDate = (date: string | Date, isUTC=true) => {
  /* Return dates in SpyGlass format for the frontend (mm/dd/yyyy).
  Set isUTC to false if you want timezone adjusted dates.

  accepts:
    1) string containing formats:
      - 'yyyy-dd-mm'
      - 'yyyy-dd-mmT14:35:25.734348+00:00'
    2) object ('Date') containing formats:
      - 'Mon Mar 17 2025 00:00:00 GMT-0400 (Eastern Daylight Time)'
      - '2023-10-05T14:30:00.000Z'

  returns: mm/dd/yyyy as string
  */

  const getUTC = (dateObject: Date) => {
    return `${String(dateObject.getUTCMonth() + 1)
      .padStart(2, '0')}/${String(dateObject.getUTCDate())
      .padStart(2, '0')}/${dateObject.getUTCFullYear()}`;
  }

  const getNonUTC = (dateObject: Date) => {
    return `${String(dateObject.getMonth() + 1)
      .padStart(2, '0')}/${String(dateObject.getDate())
      .padStart(2, '0')}/${dateObject.getFullYear()}`;
  }

  let spyGlassDate;

  try {
    let dateObject;
    switch (typeof date) {
      case 'string':
        // Dev note: if we need time, add ", time" to the below destructured array
        const [year, month, day] = date.split(/[-T]/).map(Number);
        dateObject = new Date(year, month - 1, day);
        break;
      case 'object':
        // Should be fine to pass in Object or Date
        dateObject = new Date(date);
        break;
      default:
        throw new Error("Error extracting date. Unsupported type: ", typeof date);
    }

    spyGlassDate = isUTC ? getUTC(dateObject) : getNonUTC(dateObject)
  } catch (error) {
    console.error("Error extracting date:\n", error)
    spyGlassDate = date; // use passed date as fallback
  }

  return spyGlassDate;
}

export const CUSTOMER_OWNED = "ZCUS";
export const RENTAL = "ZRNV";
export const SERVICE = "ZSER";

export function getSubcategoryBySerialized(serialized: string): SubCategory {
  return serialized === "serialized" ? SubCategory.Consumable : SubCategory.Reusable;
};

export function getUserDisplayName(userItems, id: string): string {
  if (userItems) {
    for (const user of userItems) {
      if (user.id === id) return user.display_name;
    }
  }
  return id;
}

export const sidePanelActions = {
  add: "add",
  replace: "replace",
  custom: "custom",
  none: "none"
};

// TODO: change out any instances of this being referenced with gql enum
export const statuses = {
  draft: "draft",
  published: "published",
  won: "won",
  accepted: "accepted",
  rejected: "rejected"
};

export const exclude_fields = [
  "cable",
  "housing",
  "intake",
  "motor",
  "motor_lead_extension",
  "pump",
  "seal",
  "sensor",
  "step_down_transformer",
  "step_up_transformer",
  "switchboard",
  "tubing",
  "variable_speed_drive",
  "__typename",
  "total", // XXX remove this when total is made back into a field
  "updated_at", // let hasura default to now
  "updated_at_ui", // only used as update trigger in ui
  "editing_note", // borrowing the QLI object for note editing purposes
  "editing_description", // same as note
] as (keyof QuoteLineItemType)[];

export const promiseMap = <T, R>(
  iterable: readonly T[],
  mapper: (item: T, index: number) => R | Promise<R>
): Promise<R[]> => {
  return iterable.reduce((previousPromise, item) => {
    return previousPromise.then(() => {
      return mapper(item);
    });
  }, Promise.resolve());
};

export const buildMaterialQueryFromSpyglassEquipment = (
  type,
  spyglassEquipment,
  etcs,
  quote,
  scenario,
  material_search
) => {
  const materialPricingQuery = `contract=${quote.erp_contract_number}&country=${quote.erp_well_country}&deleted=False&exclude_trade_in=True&expand=pricing&ordering=description,number&price_list=${quote.erp_price_list_number}2&pricing_date=${quote.pricing_date}&sales_org=${quote.erp_plant_id}`;
  const typeMap = {
    pump: (spyglassEquipment, etcs, quote, scenario) => {
      const hierarchy_numbers = etcs?.find(
        etc => etc.equipment_type === PUMP
      )?.hierarchy_numbers;
      const pump = scenario.equipment.r_xref_pumps.find(
        pump => pump?.pump_id === spyglassEquipment?.r_housing?.pump_id
      );
      const seriesModel = pump?.r_pump?.series_model;
      // Should be just inconel if hs is selected. No monel
      const shaft = pump?.use_high_strength_shaft ? "INC" : "";
      const searchString = [`PMP`, seriesModel.split(" ")[0], shaft].filter(Boolean).join(", ");
      // TODO: @mbenin revisit stage type for search query
      material_search.value = `${materialPricingQuery}&search=${searchString}&material__prod_hierarchy__in=${hierarchy_numbers?.join(",")}`;
      return {
        searchValue: material_search.value,
        spyglassEquipment,
        quote,
        type,
        scenario,
        searchString
      };
    },

    motor: (spyglassEquipment, etcs, quote, scenario) => {
      const hierarchy_numbers = etcs?.find(
        etc => etc.equipment_type === MOTOR
      )?.hierarchy_numbers;
      const partNumber = spyglassEquipment?.part_number.split(' ')[0];
      const horsePower = `${spyglassEquipment?.nameplate_horsepower}HP`;
      const searchString = [`MTR`, partNumber, horsePower].filter(Boolean).join(", ");
      material_search.value = `${materialPricingQuery}&search=${searchString}&material__prod_hierarchy__in=${hierarchy_numbers?.join(",")}`;
      return {
        searchValue: material_search.value,
        spyglassEquipment,
        quote,
        type,
        scenario,
        searchString
      };
    },

    intake: (spyglassEquipment, etcs, quote, scenario) => {
      const hierarchy_numbers = etcs?.find(
        etc => etc.equipment_type === INTAKE
      )?.hierarchy_numbers;
      const intakeType = spyglassEquipment?.r_intake.intake_type;
      const partNumber = spyglassEquipment?.r_intake.part_number;
      const searchString = [`INTK`, intakeType, partNumber].filter(Boolean).join(", ");

      material_search.value = `${materialPricingQuery}&search=${searchString}&material__prod_hierarchy__in=${hierarchy_numbers?.join(",")}`;
      return {
        searchValue: material_search.value,
        spyglassEquipment,
        quote,
        type,
        scenario,
        searchString
      };
    },

    seal: (spyglassEquipment, etcs, quote, scenario) => {
      const hierarchy_numbers = etcs?.find(
        etc => etc.equipment_type === SEAL
      )?.hierarchy_numbers;
      const configuration = spyglassEquipment?.r_seal?.configuration;
      const partNumber = spyglassEquipment?.r_seal?.part_number;
      const searchString = [`SEAL`, configuration, partNumber].filter(Boolean).join(", ");

      material_search.value = `${materialPricingQuery}&material__prod_hierarchy__in=${hierarchy_numbers?.join(",")}&search=${searchString}`;
      return {
        searchValue: material_search.value,
        spyglassEquipment,
        quote,
        type,
        scenario,
        searchString
      };
    },

    sensor: (spyglassEquipment, etcs, quote, scenario) => {
      const hierarchy_numbers = etcs?.find(
        etc => etc.equipment_type === SENSOR
      )?.hierarchy_numbers;
      const manufacturer = spyglassEquipment?.manufacturer;
      const searchString = [`SEN`, manufacturer].filter(Boolean).join(", ");

      material_search.value = `${materialPricingQuery}&material__prod_hierarchy__in=${hierarchy_numbers?.join(",")}&search=${searchString}`;
      return {
        searchValue: material_search.value,
        spyglassEquipment,
        quote,
        type,
        scenario,
        searchString
      };
    },

    mle: (spyglassEquipment, etcs, quote, scenario) => {
      const hierarchy_numbers = etcs?.find(
        etc => etc.equipment_type === MOTOR_LEAD_EXTENSION
      )?.hierarchy_numbers;
      const family = `Family ${spyglassEquipment?.family}`;
      const partNumber = spyglassEquipment?.part_number;
      const series = `Series ${spyglassEquipment?.series}`;
      const searchString = [`MLE`, partNumber, family, series].filter(Boolean).join(", ");
      material_search.value = `${materialPricingQuery}&material__prod_hierarchy__in=${hierarchy_numbers?.join(",")}&search=${searchString}`;
      return {
        searchValue: material_search.value,
        spyglassEquipment,
        quote,
        type,
        scenario,
        searchString
      };
    },

    cable: (spyglassEquipment, etcs, quote, scenario) => {
      const hierarchy_numbers = etcs?.find(
        etc => etc.equipment_type === CABLE
      )?.hierarchy_numbers;
      // const family = spyglassEquipment?.r_cable?.family;
      const searchString = [`CBL`].filter(Boolean).join(", ");

      material_search.value = `${materialPricingQuery}&material__prod_hierarchy__in=${hierarchy_numbers?.join(",")}&search=${searchString}`;
      return {
        searchValue: material_search.value,
        spyglassEquipment,
        quote,
        type,
        scenario,
        searchString
      };
    },

    switchboard: (spyglassEquipment, etcs, quote, scenario) => {
      const hierarchy_numbers = etcs?.find(
        etc => etc.equipment_type === SWITCHBOARD
      )?.hierarchy_numbers;
      const part_number = spyglassEquipment?.r_switchboard?.part_number;
      const searchString = [part_number].filter(Boolean).join(", ");

      material_search.value = `${materialPricingQuery}&search=${searchString}&material__prod_hierarchy__in=${hierarchy_numbers?.join(",")}`;
      return {
        searchValue: material_search.value,
        spyglassEquipment,
        quote,
        type,
        scenario,
        searchString
      };
    },

    vsd: (spyglassEquipment, etcs, quote, scenario) => {
      const hierarchy_numbers = etcs?.find(
        etc => etc.equipment_type === VSD
      )?.hierarchy_numbers;
      //const kva = spyglassEquipment?.kva;
      const searchString = [`VSD`].filter(Boolean).join(", ");;

      material_search.value = `${materialPricingQuery}&search=${searchString}`;
      return {
        searchValue: material_search.value,
        spyglassEquipment,
        quote,
        type,
        scenario,
        searchString
      };
    },

    step_up_transformer: (spyglassEquipment, etcs, quote, scenario) => {
      const hierarchy_numbers = etcs?.find(
        etc => etc.equipment_type === TRANSFORMER
      )?.hierarchy_numbers;
      const transformerType = spyglassEquipment?.transformer_type;

      const searchString = [`VFD Pad`].filter(Boolean).join(", ");
      material_search.value = `${materialPricingQuery}&search=${searchString}`;

      return {
        searchValue: material_search.value,
        spyglassEquipment,
        quote,
        type,
        scenario,
        searchString
      };
    },

    step_down_transformer: (spyglassEquipment, etcs, quote, scenario) => {
      const hierarchy_numbers = etcs?.find(
        etc => etc.equipment_type === TRANSFORMER
      )?.hierarchy_numbers;
      const transformerType =
        spyglassEquipment?.r_step_up_transformer?.transformer_type;

      const searchString = [transformerType].filter(Boolean).join(", ");

      material_search.value = `${materialPricingQuery}&search=${searchString}`;

      return {
        searchValue: material_search.value,
        spyglassEquipment,
        quote,
        type,
        scenario,
        searchString
      };
    }
  };
  return typeMap[type](spyglassEquipment, etcs, quote, scenario);
};

export const materialToQuoteLineItem = (
  materialResp,
  materialQuery,
  source,
  quote,
  sub_order
) => {
  // const material = materialResp?.data?.value?.getSKMaterials[0];
  const scenario = materialQuery.scenario;

  const quoteLineItem = {
    category: Category.Equipment,
    erp_material_long_description: materialQuery.searchString,
    // erp_material_number: material?.number,
    // erp_material_short_description: material?.description,
    // erp_price: material?.pricing?.price || null,
    // erp_product_hierarchy: material?.prod_hierarchy,
    // erp_uom: material?.so_uom,
    line_item_type: materialQuery.type,
    margin: 60,
    pricing_date: materialQuery?.quote?.pricing_date || fixDate(new Date()),
    quantity: 1,
    source,
    subcategory: SubCategory.Reusable,
    sub_order,
    parent_order: parentOrder[materialQuery.type],
    spyglass_equipment_import_json: JSON.stringify(
      materialQuery.spyglassEquipment
    )
  };

  const typeMap = {
    pump: (materialResp, materialQuery) => {
      const pumps = scenario.equipment.r_xref_pumps
        .map(pump => {
          const housings_order = pump.housings_order;
          return pump.r_xref_housings.map(housing => {
            if (housings_order.includes(housing.id)) {
              return housing;
            }
            return null;
          });
        })
        .flat()
        .filter(Boolean);

      const quoteLineItemData = {
        spyglass_pump: materialQuery?.spyglassEquipment?.r_housing?.pump_id,
        line_item_type: LineItemType.PUMP
      };
      return quoteLineItemData;
    },

    motor: (materialResp, materialQuery) => {
      const quoteLineItemData = {
        spyglass_motor: materialQuery?.spyglassEquipment?.id,
        line_item_type: LineItemType.MOTOR
      };
      return quoteLineItemData;
    },

    intake: (materialResp, materialQuery) => {
      const quoteLineItemData = {
        spyglass_intake: materialQuery?.spyglassEquipment?.r_intake.id,
        line_item_type: LineItemType.INTAKE
      };
      return quoteLineItemData;
    },

    seal: (materialResp, materialQuery) => {
      const quoteLineItemData = {
        spyglass_seal: materialQuery?.spyglassEquipment?.r_seal.id,
        line_item_type: LineItemType.SEAL
      };
      return quoteLineItemData;
    },

    sensor: (materialResp, materialQuery) => {
      const quoteLineItemData = {
        spyglass_sensor: materialQuery?.spyglassEquipment?.id,
        line_item_type: LineItemType.SENSOR
      };
      return quoteLineItemData;
    },

    mle: (materialResp, materialQuery) => {
      const quoteLineItemData = {
        spyglass_motor_lead_extension: materialQuery?.spyglassEquipment?.id,
        line_item_type: LineItemType.MLE,
      };

      let uom = materialQuery?.scenario?.display_unit_overrides["Generic.Length.Large"]

      if (materialQuery?.scenario?.display_unit_overrides["Length.convert.Feet"]) {
        uom = materialQuery?.scenario?.display_unit_overrides["Length.convert.Feet"];
      }

      uom = ToUnit(uom) || ToUnit("Feet");

      quoteLineItemData.quantity = 1;

      quoteLineItemData.erp_uom = uom?.shortLabel;

      quoteLineItemData.erp_serialized = "serialized";

      return quoteLineItemData;
    },

    cable: (materialResp, materialQuery) => {
      const quoteLineItemData = {
        spyglass_cable: materialQuery?.spyglassEquipment?.r_cable?.id,
        line_item_type: LineItemType.CABLE,
      };

      let uom = materialQuery?.scenario?.display_unit_overrides["Generic.Length.Large"]

      if (materialQuery?.scenario?.display_unit_overrides["Length.write.Feet"]) {
        uom = materialQuery?.scenario?.display_unit_overrides["Length.write.Feet"];
      }

      uom = ToUnit(uom) || ToUnit("Feet");

      quoteLineItemData.quantity = Math.ceil(convert(materialQuery?.spyglassEquipment?.length_feet, ToUnit("Feet"), uom));

      quoteLineItemData.erp_uom = uom?.shortLabel;

      return quoteLineItemData;
    },

    switchboard: (materialResp, materialQuery) => {
      const quoteLineItemData = {
        spyglass_switchboard:
          materialQuery?.spyglassEquipment?.r_switchboard?.id,
        line_item_type: LineItemType.SURFACE_DRIVE
      };
      return quoteLineItemData;
    },

    vsd: (materialResp, materialQuery) => {
      const quoteLineItemData = {
        spyglass_variable_speed_drive: materialQuery?.spyglassEquipment?.id,
        line_item_type: LineItemType.SURFACE_DRIVE
      };
      return quoteLineItemData;
    },

    step_up_transformer: (materialResp, materialQuery) => {
      const quoteLineItemData = {
        spyglass_step_up_transformer: materialQuery?.spyglassEquipment?.id,
        line_item_type: LineItemType.STEP_UP_TRANSFORMER
      };
      return quoteLineItemData;
    },

    step_down_transformer: (materialResp, materialQuery) => {
      const quoteLineItemData = {
        spyglass_step_down_transformer: materialQuery?.spyglassEquipment?.id,
        line_item_type: LineItemType.STEP_DOWN_TRANSFORMER
      };
      return quoteLineItemData;
    }
  };

  Object.assign(
    quoteLineItem,
    typeMap[materialQuery.type](materialResp, materialQuery)
  );

  if (quote?.value?.id) {
    quoteLineItem.quote_id = quote?.value?.id;
  }

  return quoteLineItem;
};

export const calculateUnitPrice = quote_line_item => {
  const unit = quote_line_item.erp_price;
  const margin = quote_line_item.margin / 100;
  const total = margin ? unit + unit * margin : unit;
  return total;
};

export const twoDecimals = (num) => {
  if (typeof(num) === "string") {
    return Number(num).toFixed(2);
  } else if (typeof(num) === "number") {
    return num.toFixed(2);
  }
};

export const formatCurrency = (num: number, currency: string | undefined | null, prependCurrencyUnit: boolean = true) => {
  num = Number(num);
  if (Number.isNaN(num)) {
    return "N/A";
  }

  // Always format the number with commas and 2 decimal places
  const numberFormat = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false
  });

  // If no currency or prependCurrency is false, just return the formatted number
  if (!prependCurrencyUnit || currency === undefined || currency === null) {
    return numberFormat.format(num);
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      currencyDisplay: "code",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  } catch (e) {
    if (e instanceof RangeError) {
      // if currency is invalid, return the number with minimal formatting
      return numberFormat.format(num);
    } else {
      throw e;
    }
  }
};

export const calculateTotal = quote_line_item => {
  const unit = calculateUnitPrice(quote_line_item);
  const qty = quote_line_item.quantity;
  const total = qty * unit;
  return total;
};

// Original Author: Christian Hall
export const fixDate = d => {
  /**  obj: [native Date]
   * val: Tue May 21 2024 08:07:11 GMT-0700 (Pacific Daylight Time)
   * returns: a LOCALE date in the format 2024-5-21 (for Hasura and SK/ERP APIs)
   *
   * Dev note: best for use on dates where we do NOT want Postgresql converting
   * the day to a "future day" from the provided timestamp.
   *
   * Also good where we want to ping SK/ERP with the given local day, not a
   * converted future UTC day.
   *
   * Examples: proposed start date, pricing date
   */

  if (d === "" || !d) {
    return "";
  }

  // As a valid date object, regardless of prior input or any validation,
  // return the format YYYY-M-D (e.g. 2024-5-21):
  const getYearMonthDayDate = (date: Date) => {
    // Dev note: many locales use D/M/YYYY (vs M/D/YYYY). Avoid parsing locale output.
    // Keep local-day semantics by using Intl parts (calendar=gregory, numerals=latn).
    const parts = new Intl.DateTimeFormat("en-US-u-ca-gregory-nu-latn", {
      year: "numeric",
      month: "numeric",
      day: "numeric"
    }).formatToParts(date);

    const values = Object.fromEntries(
      parts
        .filter(p => p.type !== "literal")
        .map(p => [p.type, p.value])
    ) as Record<string, string>;

    const year = values.year;
    const month = Number(values.month);
    const day = Number(values.day);
    const isInvalid = !year || Number.isNaN(month) || Number.isNaN(day);

    if (isInvalid) {
      // Scenario 1: broken Intl implementation
      // Scenario 2: browser incompatibility (e.g. Microsoft Edge)
      // Not sure what the precise consequences are of returning empty string,
      // but some downstream handling does have fallbacks if there's issues here.
      console.warn("fixDate: Intl.DateTimeFormat returned unexpected parts for date:", date);
      return "";
    }

    // Keep same shape as before (no zero-padding): 2024-5-21
    return `${year}-${month}-${day}`;
  };

  if (d instanceof Date) {
    const isInvalidDateFormat = Number.isNaN(d.getTime());
    if (isInvalidDateFormat) {
      console.warn("fixDate: Received an invalid Date object, likely from a cleared or malformed input:", d);
      return "";
    }
    return getYearMonthDayDate(d);
  }

  // In the specific case of string, don't use the Intl API or getYearMonthDayDate,
  // because they assume a date object, not a string, and might convert the day into a future day.
  if (typeof d === "string") {
    const dateTrimmed = d.trim();

    // Preserve already-normalized date strings (common from ERP): YYYY-M-D / YYYY-MM-DD
    const isAlreadyCorrectFormat = /^\d{4}-\d{1,2}-\d{1,2}$/.test(dateTrimmed);
    if (isAlreadyCorrectFormat) {
      return dateTrimmed;
    }

    // Normalize slash dates like 3/25/26 or 25/3/26 into YYYY-M-D.
    const dateWithSlashes = dateTrimmed.match(/^\s*(\d{1,2})\/(\d{1,2})\/(\d{2,4})\s*$/);
    if (dateWithSlashes) {
      const a = Number(dateWithSlashes[1]);
      const b = Number(dateWithSlashes[2]);
      const yRaw = Number(dateWithSlashes[3]);

      const year = dateWithSlashes[3].length === 2 ? 2000 + yRaw : yRaw;

      // Finally, after date normalization (via Intl and string),
      // check the reversed month/day scenario.
      // If one side is > 12, we can infer which is day vs month.
      // Otherwise default to M/D/Y (legacy behavior). (Author: copilot)
      let month = a;
      let day = b;
      if (a > 12 && b <= 12) {
        day = a;
        month = b;
      } else if (b > 12 && a <= 12) {
        // e.g. 3/25/26 => month=3, day=25
        month = a;
        day = b;
      }

      if (
        year >= 1900 &&
        month >= 1 &&
        month <= 12 &&
        day >= 1 &&
        day <= 31
      ) {
        return `${year}-${month}-${day}`;
      }
    }

    // Fall back to preserving the string (caller may already be handling formatting)
    return dateTrimmed;
  }

  // Best effort for other types (timestamps, etc.)
  const parsed = new Date(d);
  if (Number.isNaN(parsed.getTime())) {
    console.warn("fixDate: Could not parse non-string, non-Date value into a valid date:", d);
    return "";
  }
  return getYearMonthDayDate(parsed);
};

export const formatQuoteHeaderForSave = (quote, advancedSearch) => {
  const quoteId = quote?.id;
  const quoteName = advancedSearch.quoteSearch;
  const erpNwa = advancedSearch.nwaSearch;
  const skProposalNumber = advancedSearch.skProposalNumber;
  const user = advancedSearch.userSearch;
  const erpPriceList = advancedSearch.priceListSearch;
  const erpContract = advancedSearch.contractSearch;
  const erpPlant = advancedSearch.salesDistrictSearch;
  const erpWell = advancedSearch.wellAddressSearch;
  const pricingDate = advancedSearch.pricingDate;
  const proposedStartDate = advancedSearch.proposedStartDate;
  const currency = advancedSearch.currency;
  const versionNumber = advancedSearch.versionNumber;
  const spyglassScenario = advancedSearch.scenarioSearch;
  const skJobType = advancedSearch.skJobType;
  const status = advancedSearch.status;
  const planningOrder = advancedSearch.skPlanningOrder;
  const sk_job_number = advancedSearch.sk_job_number;
  const sk_job_id = advancedSearch.sk_job_id;
  const oldJobType = quote?.sk_job_type;
  const error_log_json = quote?.error_log_json;
  const comments = advancedSearch.comments;
  const directions = advancedSearch.directions;
  const note = advancedSearch.note;

  const quoteObj = {};
  Object.assign(quoteObj, {
    id: quoteId,
    comments,
    directions,
    note,
    currency: currency?.code,
    erp_contract_number: erpContract?.number || null,
    erp_customer_name: erpWell?.customer?.name || null,
    erp_customer_number: erpWell?.customer?.id || null,
    erp_nwa_id: erpNwa?.id,
    erp_nwa_name: erpNwa?.name,
    erp_plant_id: erpPlant?.id,
    erp_plant_name: erpPlant?.name,
    erp_price_list_name: erpPriceList?.name || null,
    erp_price_list_number: !erpPriceList?.number || erpPriceList?.number === "undefined" || erpPriceList?.number === "null" ? null : String(erpPriceList.number),
    erp_well_country: erpWell?.address?.region?.country?.code || null,
    erp_well_region: erpWell?.address?.region?.code || null,
    erp_well_name: erpWell?.well?.name || null,
    erp_well_uwi: erpWell?.well?.uwi || null,
    erp_well_id: erpWell?.well?.id || null,
    erp_address_id: erpWell?.address?.id || null,
    erp_address_name: erpWell?.address?.name || null,
    erp_full_address: erpWell?.address?.full_address || null,
    prepared_by_id: user?.id,
    customer_rep_name: advancedSearch?.repName || null,
    customer_rep_email: advancedSearch?.repEmail || null,
    customer_rep_phone_number: advancedSearch?.repPhone || null,
    // Hasura enforces a date for updateQuoteByPk, so we need to provide a default
    // one if the user clears it out beforehand. Will error out if "".
    pricing_date:
      fixDate(pricingDate) || fixDate(new Date()),
    proposed_start_date:
      fixDate(proposedStartDate) || fixDate(new Date()),
    sk_planning_order: planningOrder,
    sk_proposal_number: skProposalNumber,
    spyglass_scenario_import_json: JSON.stringify(spyglassScenario),
    spyglass_scenario_id: spyglassScenario?.id,
    sk_job_type: skJobType,
    sk_job_number,
    sk_job_id,
    status,
    version_number: versionNumber,
    name: quoteName,
    error_log_json
  });

  return quoteObj;
}

export const recalcMarginFromPriceOverride = (quote_line_item) => {
  /* hasBasePrice: we need to check if users have defined a price for their material
     hasZeroForPriceOverride: whether users enter 0 or fully backspace, it's still 0
  ***/
  const hasBasePrice = quote_line_item.erp_price && quote_line_item.erp_price != 0
  const hasZeroForPriceOverride = !Number(quote_line_item.price_override)

  if (!hasBasePrice) {
    // When ERP prices come back as 0, there is no base price to derive the margin?
    quote_line_item.margin = quote_line_item.margin;
  }
  else if (hasZeroForPriceOverride) {
    // Cannot divide by 0, so the margin by extension is 0 (unless we want to save NaN or Infinity to hasura)
    quote_line_item.margin = 0;
  }
  else {
    // Regular margin calculation
    quote_line_item.margin = (((quote_line_item.price_override - quote_line_item.erp_price) / quote_line_item.price_override) * 100).toFixed(3)
  }
}

export const formatQuoteLineItemForSave = (qli, quote_id) => {
  const qli_copy = Object.assign({}, qli);

  for (const [k, v] of Object.entries(qli_copy)) {
    if (exclude_fields.includes(k)) {
      delete qli_copy[k];
    }

    if (["pricing_date"].includes(k)) {
      /** Format to *NOT* let postgresql convert to UTC from offset / a future day
       *
       * (Probably can't be avoided. The Primevue Date picker, when changing the
       * date, is force overwriting the v-model value to native JavaScript object,
       * even if we previously saved it in the format we need).
       */
      if (!qli_copy[k]) {
        // If the date was cleared out by user, format with a default date
        // as required by Hasura. Ideal fallback is today.
        qli_copy[k] = fixDate(new Date());
      } else {
        qli_copy[k] = fixDate(qli_copy[k]); // same idea applies to quote header
      }
    }

    if (["erp_price", "price_override", "credit", "total_price_override"].includes(k)) {
      // Format decimal precision according to Hasura enforcement:
      if (qli_copy[k] == null || String(qli_copy[k]).trim() === "") {
        qli_copy[k] = null;
      } else {
        qli_copy[k] = Number(parseFloat(qli_copy[k]).toFixed(2));
      }
    }

    if (["margin"].includes(k)) {
      // Format decimal precision according to Hasura enforcement:
      if (qli_copy[k] == null || String(qli_copy[k]).trim() === "") {
        qli_copy[k] = null;
      } else {
        qli_copy[k] = Number(parseFloat(qli_copy[k]).toFixed(3));
      }
    }

    if (k === "quantity" && typeof v === "string" && v.trim() === "") {
      qli_copy[k] = null;
    }
  }

  if (isQuoteAdjustmentLineItem(qli_copy) && qli_copy.price_override != null) {
    qli_copy.price_override = getQuoteLineUnitPrice(qli_copy);
  }

  qli_copy.quote_id = quote_id;

  return qli_copy;
};

export const getDefaultMargin = (quote_line_item: any) => {
  const isService = quote_line_item.line_item_type == 'service' || quote_line_item.category == Category.Service
  const isCustomerOwned = quote_line_item.erp_material_number.startsWith('ZCUS')
  const isFromContract = quote_line_item.from_contract;  // TODO: update schema later

  if (isService || isCustomerOwned || isFromContract) {
    return 0;
  }
  else {
    return 60;
  }
}

export const isNormalQuoteLineItem = (quote_line_item: any) => {
  const isNotFree = quote_line_item.tax_type !== 'free'; // TODO fix tax type being null (how are they getting set to null?)
  const isServiceItem = [
    String(JobTypeServiceMaterialNumbers.MONTHLY),
    String(JobTypeServiceMaterialNumbers.DAILY)
  ].includes(quote_line_item.erp_material_number);

  if (isServiceItem) {
    return false;
  }

  return isNotFree;
}

export const isReusableSaleAndTradeLineItem = (quote_line_item: QuoteLineItemType) => {
  return quote_line_item.subcategory === 'reusable';
}

export const isConsumableWellTestLineItem = (quote_line_item: QuoteLineItemType) => {
  return quote_line_item.subcategory === 'consumable';
}

export const isServiceLineItemAddedFromJobType = (quote_line_item: any) => {
  const { DAILY, MONTHLY, DIRECT_SALE_MONITORING_FEE, RENTAL_MONITORING_FEE } = JobTypeServiceMaterialNumbers;
  const serviceMaterialNumbers = [
    String(DAILY),
    String(MONTHLY),
    String(DIRECT_SALE_MONITORING_FEE),
    String(RENTAL_MONITORING_FEE)
  ];

  const {
    DAILY:DAILY_LONG_DESC,
    MONTHLY:MONTHLY_LONG_DESC,
    DIRECT_SALE_MONITORING_FEE:DIRECT_SALE_MONITORING_FEE_LONG_DESC,
    RENTAL_MONITORING_FEE:RENTAL_MONITORING_FEE_LONG_DESC
  } = JobTypeServiceMaterialLongDesc;
  const serviceDescriptions = [
    DAILY_LONG_DESC,
    MONTHLY_LONG_DESC,
    DIRECT_SALE_MONITORING_FEE_LONG_DESC,
    RENTAL_MONITORING_FEE_LONG_DESC
  ];

  // Note: removal functions are filtering by the aforementioned material numbers
  // only. So it's safe to assume those numbers are the correct condition to check
  // against until Job Type / its spawned materials' spec is further refined.
  const hasMn = serviceMaterialNumbers.includes(quote_line_item.erp_material_number);
  const hasDesc = (
    serviceDescriptions.includes(quote_line_item.erp_material_short_description) ||
    serviceDescriptions.includes(quote_line_item.erp_material_long_description)
  );
  const hasCategories = quote_line_item.category === Category.Service && quote_line_item.subcategory === SubCategory.Reusable;
  const isServiceMaterialAddedFromJobType =
    hasCategories && (hasMn || hasDesc);
  return isServiceMaterialAddedFromJobType;
}

const hasGroup =
  typeof Object.groupBy === typeof undefined ||
  typeof Array.groupToMap === typeof undefined ||
  typeof Array.group === typeof undefined;
if (!hasGroup) {
  const groupBy = (arr, callback) => {
    return arr.reduce((acc = {}, ...args) => {
      const key = callback(...args);
      acc[key] ??= [];
      acc[key].push(args[0]);
      return acc;
    }, {});
  };

  if (typeof Object.groupBy === typeof undefined) {
    Object.groupBy = groupBy;
  }

  if (typeof Array.groupToMap === typeof undefined) {
    Array.groupToMap = groupBy;
  }

  if (typeof Array.group === typeof undefined) {
    Array.group = groupBy;
  }
}

export function pick(obj, keys) {
  const ret = {};
  for (const key of keys) {
    ret[key] = obj[key];
  }
  return ret;
}

export const checkValidNumberInput = (event: KeyboardEvent, allowDecimals: boolean = true, allowNegatives: boolean = true) => {
  // allowDecimals: okay to allow decimals in majority of use cases.
  // In some cases we shouldn't allow decimals like the quantity input.
  let allowedCharacters;
  if (allowDecimals && allowNegatives) {
    allowedCharacters = /^[0-9.-]$/;
  }
  else if (allowDecimals) {
    allowedCharacters = /^[0-9.]$/;
  }
  else if (allowNegatives) {
    allowedCharacters = /^[0-9-]$/;
  }
  else {
    allowedCharacters = /^[0-9]$/;
  }

  const userUsingInvalidData =
    !allowedCharacters.test(event.key) && !isSpecialAllowedKey(event);

  if (userUsingInvalidData) {
    event.preventDefault();
    return false
  }

  return true
};

export const checkValidPhoneInput = (event: KeyboardEvent | ClipboardEvent): void => {
  if (event instanceof KeyboardEvent) {
    const key = event.key;

    if (isSpecialAllowedKey(event)) {
      return;
    }

    if (/^\d$/.test(key)) {
      return;
    }

    event.preventDefault();
  } else if (event instanceof ClipboardEvent) {
    // Handle paste event
    const clipboardData = event.clipboardData?.getData("text") || "";
    const sanitizedData = clipboardData.replace(/[^\d]/g, "");

    if (sanitizedData !== clipboardData) {
      event.preventDefault();
      const inputElement = event.target as HTMLInputElement;
      const cursorPosition = inputElement.selectionStart || 0;

      const currentValue = inputElement.value;
      inputElement.value =
        currentValue.slice(0, cursorPosition) +
        sanitizedData +
        currentValue.slice(cursorPosition);
      inputElement.setSelectionRange(
        cursorPosition + sanitizedData.length,
        cursorPosition + sanitizedData.length
      );
    }
  }
};

export const sanitizePastedNumberInput = (event: ClipboardEvent) => {
  event.preventDefault();

  const clipboardData = event.clipboardData || (window as any).clipboardData;
  const pastedText = clipboardData.getData("text") || "";
  const sanitizedText = pastedText.replace(/\D/g, "");

  if (event.target instanceof HTMLInputElement) {
    event.target.value = sanitizedText;

    // Manually trigger an input event to update v-model. Need this since we used event.preventDefault()
    event.target.dispatchEvent(new Event("input"));
  }
};

const isSpecialAllowedKey = (event: KeyboardEvent) => {
  // Having tested the inputs on the quote table, these are allowable
  // from a UX perspective. Remove a special key if there is a reason
  // to disallow them.
  const specialAllowedKeys = [
    "Backspace",
    "Delete",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Tab",
    "Enter",
    "Shift",
    "Control",
    "Alt",
    "Meta",
    "Home",
    "End",
    "Period",
    "Comma",
  ];

  return specialAllowedKeys.includes(event.key) || event.ctrlKey || event.metaKey;
};

/**
 * This function formats an SkBillable and returns an SGQ-formatted quote line item.
 *
 * Contains various useful utility such as calculating the sub_order against the
 * quote's current state of quote line items.
 *
 * Dev note: if you are running into issues with the Quote Design List <DataTable>
 * not displaying your formatted billable correctly, you probably need to:
 * a) resortLocalQuoteLineItems() against your quote line items list.
 * b) pass in the correct line item type before sub_order calculation.
 *
 * @param {SkBillable} billable - The SK / abstract Billable to translate / format
 * @param {LineItemType} line_item_type - Not an SK-level or SkBillable-level prop.
 * Find a way to calculate the line item type and pass it here.
 * TODO: maybe look into a way to calculate the line item type in here, but for now
 * there's a lot of specific requirements, not sure if it's re-usable enough yet.
 * @param {Quotes} quote - The quote object needed for various calculations
 * @returns The SK / abstract Billable in SpyGlass Quote format
 */
export const translateBillableToQuoteLineItem = (
  billable: SkBillable,
  line_item_type: LineItemType,
  quote: QuoteType
): QuoteLineItemType => {
  const quoteLineItem = {} as QuoteLineItemType;

  quoteLineItem.quote_id = quote?.id;
  quoteLineItem.erp_material_long_description =
    billable?.material_long_description || billable?.material_description || "";
  quoteLineItem.erp_material_short_description = billable?.material_description || "";
  quoteLineItem.erp_material_number = billable?.material_number || "";
  quoteLineItem.erp_price = Number(billable?.price) || 0;
  quoteLineItem.price_override = quoteLineItem.erp_price;

  quoteLineItem.erp_product_hierarchy = billable?.prod_hierarchy || "";
  quoteLineItem.erp_uom = billable?.uom || "FT";

  quoteLineItem.line_item_type = line_item_type || LineItemType.UNCATEGORIZED;
  quoteLineItem.parent_order = parentOrder[quoteLineItem.line_item_type];
  quoteLineItem.erp_serialized = billable?.serialized ? "serialized" : "unserialized";
  quoteLineItem.category = billable?.material_type === "ZSER"
      ? Category.Service
      : Category.Equipment; // if billable needs .category, we would substitute it here (billable.category || Categories.EQUIPMENT)

  // TODO: credit is not a field on billable, unknown if needed
  quoteLineItem.credit = 0;

  quoteLineItem.margin = getDefaultMargin(quoteLineItem);
  quoteLineItem.note = billable?.notes_description || "";
  quoteLineItem.quantity = Number(billable?.quantity) || 1;
  quoteLineItem.source = "change_order";
  quoteLineItem.subcategory = getSubcategoryBySerialized(quoteLineItem.erp_serialized);
  quoteLineItem.sub_order = getAddedMaterialSubOrder(quoteLineItem.line_item_type, quote);
  quoteLineItem.tax_type = "taxed";
  quoteLineItem.pricing_date = fixDate(billable?.pricing_date);
  // Note: Hasura requires this, but not applicable to an SK / abstract billable
  quoteLineItem.spyglass_equipment_import_json = "";

  return quoteLineItem;
}

/**
 * This function returns a calculated sub_order for a new quote line item.
 * It takes into account your quote's current state of quote line items.
 *
 * Only information required from the quote line item is the line item type.
 *
 * @param {LineItemType} line_item_type - your designated quote line item type to calculate on.
 * @param {Quotes} quote - The quote object needed for various calculations.
 * @returns A sub_order as a number.
 */
export function getAddedMaterialSubOrder(line_item_type: string, quote: any): number {
  const quoteLineItems = quote?.quote_line_items;
  const quoteHasEquipmentType = quoteLineItems
    .filter(qli => qli.line_item_type)
    .find(qli => qli.line_item_type === line_item_type);

  if (quoteHasEquipmentType) {
    const allSubOrdersOfEquipmentType = quoteLineItems
      .filter(quote => quote.line_item_type === line_item_type)
      .map(quote => quote.sub_order);
    return Math.max(...allSubOrdersOfEquipmentType) + 1;
  } else if (quoteLineItems.length > 0) {
    const newParentOrder = parentOrder[line_item_type];
    const allParentOrders = quoteLineItems
      .filter(qli => qli.line_item_type)
      .map(qli => qli.parent_order);

    let closestParentOrder = Math.max(...allParentOrders);
    for (let i = 0; i < allParentOrders.length; i++) {
      const newClosestParent =
        allParentOrders[i] >= newParentOrder &&
        allParentOrders[i] < closestParentOrder;
      newClosestParent
        ? (closestParentOrder = allParentOrders[i])
        : closestParentOrder; // <--- The important part: this gives us the next closest line item type in our quote list
    }

    const allSubordersOfClosest = quoteLineItems
      .filter(qli => qli.parent_order === closestParentOrder)
      .map(qli => qli.sub_order || 0);
    if (newParentOrder > closestParentOrder) {
      return Math.max(...allSubordersOfClosest) + 1;
    } else {
      return Math.min(...allSubordersOfClosest);
    }
  } else {
    // No quote line items, and by no extension no equipment types,
    // so return a default of suborder 1
    return 1;
  }
};

/**
 * This function returns a correctly resorted list of quote line items
 * if it's out of sync with sub_orders and parent_orders.
 *
 * Needed for proper UI output on the Quote Design List <DataTable>.
 * Probably needed in general if you modify your quote's list of QLIs.
 *
 * @param {QuoteLineItemType[]} quote_line_items - An array of quote line items.
 * @returns {QuoteLineItemType[]} A list of quote_line_items with resorted sub_orders and parent_orders.
 */
export const resortLocalQuoteLineItems = (quote_line_items: QuoteLineItemType[]): QuoteLineItemType[] => {
  // TODO: substitute the replace action with this function and test it
  const sorted_quote_line_items = quote_line_items
    .sort((a: QuoteLineItemType, b: QuoteLineItemType) => {
      if (a.parent_order === b.parent_order) {
        return a.sub_order - b.sub_order;
      }
      return a.parent_order - b.parent_order;
    })

  sorted_quote_line_items
    .forEach((qli: QuoteLineItemType) => {
      qli.sub_order = quote_line_items.indexOf(qli) + 1;
    })

  return sorted_quote_line_items;
}

/**
 * Returns the line item by the material's description.
 *
 * This serves as a fallback if there are no matches with the material's
 * product hiearchy against the returned equipment type conversions.
 *
 * Feel free to add more cases if you discover more patterns.
 *
 * @param {string | null | undefined} - A short or long description.
 * @returns {undefined | LineItemType | null} - The respective LineItemType,
 * or null or undefined if there is no match (the distinction might be important).
 */
export const getLineItemTypeByDescription = (description: string | null | undefined): undefined | LineItemType | null => {
  if (!description || description === "") {
    return undefined;
  }

  if (description.includes("PMP")) {
    return LineItemType.PUMP;
  } else if (description.includes("CABLE") || description.includes("CBL")) {
    return LineItemType.CABLE;
  } else if (description.includes("MTR")) {
    return LineItemType.MOTOR;
  } else if (description.includes("MLE")) {
    return LineItemType.MLE;
  } else {
    return null;
  }
}

// Product Hierarchy Equipment Types
export const PUMP = "PMP";
export const GAS_SEP = "IG";
export const INTAKE = "INT"; // TODO: add in SK
export const SEAL = "SL";
export const MOTOR = "MTR";
export const SENSOR = "SE0";
export const CABLE = "CAB";
export const MOTOR_LEAD_EXTENSION = "MLE";
export const CAP_TUBE = "CT"
export const VSD = "VSD";
export const SWITCHBOARD = "SW";
export const TRANSFORMER = "TRF";
export const FILTERS = "FTR";
export const PROTECTORS_SEALS = "SLPR";
export const SINE_WAVE_FILTER = "SWF";
export const MISC = "MI";

// Pump
// Gas Sep
// Intake
// Seal
// Motor
// Sensor
// Cable
// MLE
// Capillary
// Surface Drive
// Step up Transformer
// Step Down Transformer
// Sine Wave Filter
// Sine Wave
// Filters
// Misc
// Uncategorized (this should be able to be placed or adopted into any of the above categories if required

// Why so many line item type definitions? There's a list of line item types defined as
// product hierarchies in the sk equipment type conversions, and then there's the types
// that we are directly to categorized by in Spyglass (similar but not the same).
// LineItemType:  ProductHierarchyType
export enum LineItemType {
  PUMP = "pump",
  GAS_SEPARATOR = "gas_separator",
  INTAKE = "intake",
  SEAL = "seal",
  MOTOR = "motor",
  SENSOR = "sensor",
  CABLE = "cable",
  MLE = "mle",
  CAPILLARY_TUBE = "capillary_tube",
  SURFACE_DRIVE = "surface_drive",
  STEP_UP_TRANSFORMER = "step_up_transformer",
  STEP_DOWN_TRANSFORMER = "step_down_transformer",
  TRANSFORMER = "transformer",
  SINE_WAVE_FILTER = "sine_wave_filter",
  SINE_WAVE = "sine_wave",
  FILTERS = "filters",
  PROTECTORS_SEALS = "protectors_seals",
  MISCELLANEOUS = "miscellaneous",
  UNCATEGORIZED = "uncategorized",
  SWITCHBOARD = "switchboard",
  VSD = "vsd"
}

export const parentOrder = {
  [LineItemType.PUMP]: 1,
  [LineItemType.GAS_SEPARATOR]: 2,
  [LineItemType.INTAKE]: 3,
  [LineItemType.SEAL]: 4,
  [LineItemType.MOTOR]: 5,
  [LineItemType.SENSOR]: 6,
  [LineItemType.CABLE]: 7,
  [LineItemType.MLE]: 8,
  [LineItemType.CAPILLARY_TUBE]: 9,
  [LineItemType.SURFACE_DRIVE]: 10,
  [LineItemType.STEP_UP_TRANSFORMER]: 11,
  [LineItemType.STEP_DOWN_TRANSFORMER]: 12,
  [LineItemType.TRANSFORMER]: 13,
  [LineItemType.SINE_WAVE_FILTER]: 14,
  [LineItemType.SINE_WAVE]: 15,
  [LineItemType.FILTERS]: 16,
  [LineItemType.PROTECTORS_SEALS]: 17,
  [LineItemType.MISCELLANEOUS]: 18,
  [LineItemType.UNCATEGORIZED]: 19
} // TODO: missing switchboard and VSD

// Note: Order starts at 1
export const line_item_order = [
  "pump",
  "gas_separator",
  "intake",
  "seal",
  "motor",
  "sensor",
  "cable",
  "mle",
  "capillary_tube",
  "surface_drive",
  "step_up_transformer",
  "step_down_transformer",
  "transformer",
  "sine_wave_filter",
  "sine_wave",
  "filters",
  "protectors_seals",
  "miscellaneous",
  "uncategorized"
]; // TODO: missing switchboard and VSD
// TODO: let users move transformer (hierarchy) to step up or step down (equipment type)

export const getLineItemTypeLabel = (lineItemType) => {
  return lineItemType
    .toUpperCase()
    .replaceAll("_", " ")
};

export const getLineItemTypeLabelCamel = (lineItemType) => {
  return capFirstLetter(lineItemType)
    .replaceAll("_", " ")
};

export const capFirstLetter = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const isQuantityOneMaterial = (material: {
  erp_serialized?: string | null;
  serialized?: boolean | null;
  erp_product_hierarchy?: string | null;
  prod_hierarchy?: string | null;
}, conversions: readonly (SkEquipmentTypeConversion | null)[] = []): boolean => {
  const serialized =
    material.erp_serialized === "serialized" || material.serialized === true;
  if (!serialized) return false;
  const hierarchy = material.erp_product_hierarchy || material.prod_hierarchy;
  if (!hierarchy) return true;
  const matches = conversions.filter(
    (conversion): conversion is SkEquipmentTypeConversion =>
      Boolean(conversion?.hierarchy_numbers?.some(number => number?.startsWith(hierarchy)))
  );
  return !matches.length || matches.some(conversion => {
    const type = getLineItemTypeByProductHierarchy(hierarchy, [conversion]);
    return type !== LineItemType.CABLE && type !== LineItemType.CAPILLARY_TUBE;
  });
};

/**
 * Returns a line item type based on the product hierarchy
 *
 * Requires passing in a returned a array of SK Equipment Type Conversions.
 * Yes we could fetch them from in this file, but I don't know how the team
 * feels about putting the API call in this quoteHelpers.ts file.
 *
 * @param {string} erp_product_hierarchy - The ERP product hierarchy to check.
 * Note: run SkMaterials to get product hierarchies.
 * @returns {LineItemType | undefined} - Return the LineItemType if there is a
 * match, otherwise return undefined (so that we can leverage fallbacks).
 */
export const getLineItemTypeByProductHierarchy = (erp_product_hierarchy: string, etcs: SkEquipmentTypeConversion[]): LineItemType | undefined => {
  const equipmentTypeMap: { [key: string]: LineItemType } = {
    [PUMP]: LineItemType.PUMP,
    [GAS_SEP]: LineItemType.GAS_SEPARATOR,
    [INTAKE]: LineItemType.INTAKE,
    [SEAL]: LineItemType.SEAL,
    [MOTOR]: LineItemType.MOTOR,
    [SENSOR]: LineItemType.SENSOR,
    [CABLE]: LineItemType.CABLE,
    [MOTOR_LEAD_EXTENSION]: LineItemType.MLE,
    [CAP_TUBE]: LineItemType.CAPILLARY_TUBE,
    [TRANSFORMER]: LineItemType.TRANSFORMER,
    [FILTERS]: LineItemType.FILTERS,
    [PROTECTORS_SEALS]: LineItemType.PROTECTORS_SEALS,
    [SINE_WAVE_FILTER]: LineItemType.SINE_WAVE_FILTER,
    [MISC]: LineItemType.MISCELLANEOUS,
  };

  let etc: any = null;
  etcs.find((type_conversion: SkEquipmentTypeConversion) => {
    return type_conversion?.hierarchy_numbers?.forEach((hierarchy_num: any) => {
      if (hierarchy_num?.startsWith(erp_product_hierarchy)) {
        etc = type_conversion;
      }
    });
  });

  if (!isEmpty(etc) && etc.equipment_type) {
    return equipmentTypeMap[etc.equipment_type];
  }

  return undefined
}

export const SKQuoteLineItemFields = [
  "created_at",
  "credit",
  "erp_material_long_description",
  "erp_material_number",
  "erp_material_short_description",
  "erp_price",
  "erp_product_hierarchy",
  "erp_uom",
  "margin",
  "note",
  "parent_order",
  "price_override",
  "pricing_date",
  "quantity",
  "sub_order",
  "tax_type",
  "subcategory"
] as (keyof QuoteLineItemType)[]

export const SKQuoteFields = [
  "created_at",
  "currency",
  "deleted_at",
  "erp_address_id",
  "erp_address_name",
  "erp_contract_number",
  "erp_customer_name",
  "erp_customer_number",
  "erp_full_address",
  "erp_nwa_id",
  "erp_nwa_name",
  "erp_plant_id",
  "erp_plant_name",
  "erp_price_list_name",
  "erp_price_list_number",
  "erp_well_country",
  "erp_well_id",
  "erp_well_name",
  "erp_well_region",
  "erp_well_uwi",
  "name",
  "pricing_date",
  "proposed_start_date",
  "sk_job_id",
  "sk_job_number",
  "sk_planning_order",
  "sk_proposal_number",
  "tax_type",
  "updated_at",
  "version_number"
]

/**
 * Determines if a quote has child quotes
 */
export const hasChildren = (quote: any): boolean => {
  if (!quote || !('children' in quote)) return false;
  return Array.isArray(quote.children) && quote.children.length > 0;
};

/**
 * Determines if a quote is a root quote (has no parent)
 */
export const isRootQuote = (quote: Quotes): boolean => !quote.parent;

/**
 * Sorts quotes to ensure parents come before their children
 * Used for filtered mode display
 */
export const sortQuotesWithHierarchy = (quotes: Quotes[]): Quotes[] => {
  return [...quotes].sort((a, b) => {
    // If one is parent and the other is its child, parent comes first
    if (!a.parent && b.parent && b.parent.id === a.id) return -1;
    if (!b.parent && a.parent && a.parent.id === b.id) return 1;

    // If both are children of the same parent, sort by version_number
    if (a.parent && b.parent && a.parent.id === b.parent.id) {
      return (a.version_number || 0) - (b.version_number || 0);
    }

    // Otherwise maintain original order
    return 0;
  });
};

/**
 * @param {string} sk_job_type - the selected job type provided by quote object
 * @returns {boolean} - true if job type is well test/rental, false if not
 */
export const isJobTypeWellTestRental = (sk_job_type: string) => {
  return [SKJobType.Spyglass_1092000, SKJobType.Spyglass_1092002]
    .includes(sk_job_type)
}

/**
 * @param {object | any} val - the object or vue object that has a deep nested reactive proxy object
 * @returns {Array} - return the raw, direct JavaScript Array variant of the nested reactive proxy object
 * Example: quote?.value?.quote_line_items = getArrayFromDeepNestedProxy(quote?.value?.quote_line_items)
 */
export function getArrayFromDeepNestedProxy<T>(val: T, seen = new WeakMap()): T {
  if (val === null || typeof val !== 'object') return val;

  // Avoid infinite loops on cyclic graphs
  if (seen.has(val as any)) return seen.get(val as any);

  const raw = isProxy(val) ? toRaw(val) : val;
  const clone: any = Array.isArray(raw) ? [] : Object.create(Object.getPrototypeOf(raw));
  seen.set(val as any, clone);

  for (const key of Object.keys(raw)) {
    // Dev note: this is recursive
    clone[key] = getArrayFromDeepNestedProxy((raw as any)[key], seen);
  }
  return clone;
}

export const isMissingRequiredFields = (advancedSearch: Ref<QuotesSearch> | undefined) => {
  const value = advancedSearch?.value;
  return !(
    (value?.priceListSearch?.name || value?.priceListSearch?.id) && // Price List
    value?.pricingDate && // Pricing Date
    value?.salesDistrictSearch?.id &&  //Sales District
    value?.nwaSearch?.id && // NWA
    (value?.currency?.code || value?.currency?.name) && // Currency
    value?.userSearch?.id // Prepared By
  );
}

export const getEventTargetValue = (e: Event): string => {
  /* This function is to help with various typescript errors when trying to
   * retrieve the value from event targets. Officially Event does not define
   * value on the default target, only targets HTMLInputElement, HTMLTextAreaElement,
   * etc. does so just use this function when accessing value and ts is happy.
   *
   * Ex.
   * ```
   * const onSearchInput = (e: Event) => {
   *   if (getEventTargetValue(e) === "") {...}
   *  }
   * ```
   */
  type InputElement =
    | HTMLInputElement
    | HTMLTextAreaElement
    | HTMLSelectElement
    | HTMLButtonElement
    | HTMLOptionElement
    | HTMLProgressElement
    | HTMLMeterElement
    | HTMLOutputElement;

  // Input element types are all HTML elements that have value as a prop
  return (
    (e.target as InputElement)?.value ||
    (e.currentTarget as InputElement)?.value ||
    ""
  );
};
